/**
 * Shared UI pieces for the economy, bounty, journal, settings and travel
 * panels: game context, commits, item cells (same art as the bag), coins,
 * prices, steppers, bars and a portal tooltip.
 */
import { signal } from '@preact/signals';
import type { ComponentChildren } from 'preact';
import { createPortal } from 'preact/compat';
import { useEffect, useState } from 'preact/hooks';
import type { AccountSave } from '../../game/account';
import type { CharacterSave } from '../../game/character';
import type { Game } from '../../game/Game';
import type { Cost } from '../../game/inventory';
import {
  type EquipItem,
  type ItemDef,
  type Rarity,
  ITEMS,
  RARITY_COLOR,
  SET_NAMES,
  gearIlvl,
} from '../../game/items';
import type { Progression } from '../../game/Progression';
import { api } from '../../ui/app/api';
import { bump, ui } from '../../ui/app/store';
import { itemIcon } from '../build/art';
import { fmt } from './bag';
import './ui.css';

let gameRef: Game | null = null;

/** Called from each feature's install(). */
export function setGame(g: Game): void {
  gameRef = g;
}

export const getGame = (): Game | null => gameRef;

export interface Ctx {
  p: Progression;
  c: CharacterSave;
  a: AccountSave;
}

/** Current character context; subscribes the component to `ui.version`. */
export function useCtx(): Ctx | null {
  void ui.version.value;
  const p = currentProgression();
  return p ? { p, c: p.char, a: p.account } : null;
}

function currentProgression(): Progression | null {
  try {
    return api().progression;
  } catch {
    return null;
  }
}

/** Re-render panels and schedule a save after a mutation. */
export function commit(save = true): void {
  bump();
  if (save && gameRef) gameRef.store.saveSoon(gameRef.account);
}

export function toast(text: string, color?: string): void {
  try {
    api().notify(text, color);
  } catch {
    /* no game (previews) */
  }
}

export function confirmThen(text: string, okLabel: string, ok: () => void): void {
  ui.confirm.value = { text, okLabel, ok };
}

/** Re-renders every `ms` (timers, countdowns). */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), ms);
    return () => clearInterval(t);
  }, [ms]);
  return now;
}

export { fmt };

export function compact(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(n >= 10_000_000 ? 0 : 1)}m`;
  if (n >= 10_000) return `${Math.floor(n / 1000)}k`;
  return String(n);
}

export function timeAgo(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 60) return 'just now';
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function duration(ms: number): string {
  const s = Math.max(0, Math.ceil(ms / 1000));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  if (m > 0) return `${m}m ${String(s % 60).padStart(2, '0')}s`;
  return `${s}s`;
}

// ---------------------------------------------------------------- tooltip

interface TipState {
  x: number;
  y: number;
  body: () => ComponentChildren;
}
const tipSig = signal<TipState | null>(null);

/** Hover handlers that show `body` in the shared tooltip. */
export function tip(body: () => ComponentChildren): {
  onMouseEnter: (e: MouseEvent) => void;
  onMouseMove: (e: MouseEvent) => void;
  onMouseLeave: () => void;
} {
  return {
    onMouseEnter: (e) => (tipSig.value = { x: e.clientX, y: e.clientY, body }),
    onMouseMove: (e) => {
      const t = tipSig.value;
      if (t) tipSig.value = { ...t, x: e.clientX, y: e.clientY };
    },
    onMouseLeave: () => (tipSig.value = null),
  };
}

/** Render once inside each window; the tooltip itself is portalled out of the (transformed) window. */
export function TipLayer() {
  useEffect(() => () => void (tipSig.value = null), []);
  const t = tipSig.value;
  const host = typeof document !== 'undefined' ? document.getElementById('ui-app') : null;
  if (!t || !host) return null;
  const w = 300;
  const left = t.x + 18 + w > window.innerWidth ? t.x - w - 14 : t.x + 18;
  const top = Math.min(t.y + 14, window.innerHeight - 220);
  return createPortal(
    <div
      class="fx-tip"
      style={{ left: `${left}px`, top: `${Math.max(8, top)}px`, width: `${w}px` }}
    >
      {t.body()}
    </div>,
    host,
  );
}

const KIND_LABEL: Record<string, string> = {
  material: 'Material',
  consumable: 'Consumable',
  battle: 'Battle item',
  book: 'Codex',
  skillpotion: 'Tonic',
  gem: 'Gem',
  card: 'Card',
  chest: 'Chest',
  quest: 'Quest item',
  token: 'Token',
  food: 'Food',
  junk: 'Junk',
  tool: 'Tool',
};

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** Tooltip body for a catalog item or equipment instance. */
export function ItemTip({
  item,
  extra,
}: {
  item: EquipItem | ItemDef | string;
  extra?: ComponentChildren;
}) {
  const def = typeof item === 'string' ? ITEMS[item] : 'uid' in item ? null : item;
  if (def) {
    return (
      <>
        <div class="fx-tip-h" style={{ color: RARITY_COLOR[def.rarity] }}>
          {def.name}
        </div>
        <div class="fx-tip-b">
          <div class="fx-tip-kind">
            {cap(def.rarity)} {KIND_LABEL[def.kind] ?? def.kind}
          </div>
          <p>{def.description}</p>
          {def.battle && (
            <div class="fx-tip-dim">
              Cooldown {def.battle.cooldown}s · {def.battle.limit} per instance
            </div>
          )}
          {def.value > 0 && <div class="fx-tip-dim">Sells for {fmt(def.value)} silver each</div>}
          {extra}
        </div>
      </>
    );
  }
  if (typeof item === 'string' || !('uid' in item)) return null;
  return (
    <>
      <div class="fx-tip-h" style={{ color: RARITY_COLOR[item.rarity] }}>
        {item.kind === 'gear' && item.plus > 0 ? `+${item.plus} ` : ''}
        {item.name}
      </div>
      <div class="fx-tip-b">
        <div class="fx-tip-kind">
          {cap(item.rarity)}{' '}
          {item.kind === 'gear'
            ? cap(item.slot)
            : item.kind === 'accessory'
              ? cap(item.slot)
              : 'Rune stone'}
        </div>
        {item.kind === 'gear' && (
          <>
            <div class="fx-tip-line">
              <span>Item level</span>
              <b>{gearIlvl(item)}</b>
            </div>
            <div class="fx-tip-line">
              <span>Quality</span>
              <b>{item.quality}</b>
            </div>
            {item.set && <div class="fx-tip-set">{SET_NAMES[item.set] ?? item.set} set</div>}
          </>
        )}
        {item.kind === 'accessory' && (
          <div class="fx-tip-line">
            <span>Item level</span>
            <b>{item.ilvl}</b>
          </div>
        )}
        {extra}
      </div>
    </>
  );
}

// ---------------------------------------------------------------- cells & money

export const rarityOf = (item: EquipItem | ItemDef | string): Rarity =>
  typeof item === 'string' ? (ITEMS[item]?.rarity ?? 'normal') : item.rarity;

/** Square item cell on its rarity gradient (same art as the bag). */
export function Cell({
  item,
  count,
  size = 40,
  dim,
  tipExtra,
  noTip,
}: {
  item: EquipItem | ItemDef | string;
  count?: number;
  size?: number;
  dim?: boolean;
  tipExtra?: ComponentChildren;
  noTip?: boolean;
}) {
  const r = rarityOf(item);
  const plus =
    typeof item !== 'string' && 'uid' in item && item.kind === 'gear' && item.plus > 0
      ? item.plus
      : 0;
  return (
    <span
      class={`fx-cell rbg-${r}${dim ? ' is-dim' : ''}`}
      style={{ width: `${size}px`, height: `${size}px` }}
      {...(noTip ? {} : tip(() => <ItemTip item={item} extra={tipExtra} />))}
    >
      <img src={itemIcon(item)} alt="" draggable={false} />
      {plus > 0 && <b class="fx-cell-plus">+{plus}</b>}
      {count !== undefined && count > 1 && <b class="fx-cell-n">{compact(count)}</b>}
    </span>
  );
}

/** Silver / gold coin or a token icon. */
export function Coin({ kind, size = 14 }: { kind: string; size?: number }) {
  if (kind === 'silver' || kind === 'gold')
    return <i class={`fx-coin ${kind}`} style={{ width: `${size}px`, height: `${size}px` }} />;
  return (
    <img
      class="fx-token"
      src={itemIcon(kind)}
      alt=""
      style={{ width: `${size + 4}px`, height: `${size + 4}px` }}
    />
  );
}

export function Amount({
  kind,
  n,
  short = false,
  bad = false,
}: {
  kind: string;
  n: number;
  short?: boolean;
  bad?: boolean;
}) {
  return (
    <span class={`fx-amt${bad ? ' is-bad' : ''}`} title={ITEMS[kind]?.name ?? kind}>
      <Coin kind={kind} />
      {short ? compact(n) : fmt(n)}
    </span>
  );
}

/** A cost line; parts the player can't cover turn red. */
export function Price({ cost, c, a }: { cost: Cost; c?: CharacterSave; a?: AccountSave }) {
  const parts: { kind: string; n: number; bad: boolean }[] = [];
  if (cost.silver)
    parts.push({ kind: 'silver', n: cost.silver, bad: !!a && a.wallet.silver < cost.silver });
  if (cost.gold) parts.push({ kind: 'gold', n: cost.gold, bad: !!a && a.wallet.gold < cost.gold });
  for (const [id, n] of Object.entries(cost.items ?? {}))
    if (n > 0) parts.push({ kind: id, n, bad: !!c && (c.inventory.stacks[id] ?? 0) < n });
  return (
    <span class="fx-price">
      {parts.map((x) => (
        <Amount key={x.kind} kind={x.kind} n={x.n} bad={x.bad} />
      ))}
    </span>
  );
}

/** Wallet strip: silver, gold and any tokens relevant to the panel. */
export function Wallet({
  a,
  c,
  tokens = [],
}: {
  a: AccountSave;
  c: CharacterSave;
  tokens?: string[];
}) {
  return (
    <div class="fx-wallet">
      <Amount kind="silver" n={a.wallet.silver} />
      <Amount kind="gold" n={a.wallet.gold} />
      {tokens.map((t) => (
        <Amount key={t} kind={t} n={c.inventory.stacks[t] ?? 0} />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------- controls

export function Stepper({
  value,
  min = 1,
  max,
  onChange,
  step = 1,
}: {
  value: number;
  min?: number;
  max: number;
  onChange: (n: number) => void;
  step?: number;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(Math.max(min, max), Math.round(n)));
  return (
    <span class="fx-stepper">
      <button
        type="button"
        class="fx-step"
        disabled={value <= min}
        onClick={() => onChange(clamp(value - step))}
        aria-label="Less"
      >
        −
      </button>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onInput={(e) => {
          const n = Number(e.currentTarget.value);
          if (Number.isFinite(n)) onChange(clamp(n));
        }}
      />
      <button
        type="button"
        class="fx-step"
        disabled={value >= max}
        onClick={() => onChange(clamp(value + step))}
        aria-label="More"
      >
        +
      </button>
      <button
        type="button"
        class="fx-step fx-step-max"
        disabled={value >= max}
        onClick={() => onChange(clamp(max))}
      >
        Max
      </button>
    </span>
  );
}

export function Bar({
  value,
  max,
  tone = 'gold',
  label,
}: {
  value: number;
  max: number;
  tone?: 'gold' | 'blue' | 'green' | 'violet' | 'red';
  label?: string;
}) {
  const pct = max > 0 ? Math.max(0, Math.min(1, value / max)) : 0;
  return (
    <span class={`fx-bar fx-bar--${tone}`}>
      <i style={{ transform: `scaleX(${pct})` }} />
      {label !== undefined && <span>{label}</span>}
    </span>
  );
}

export function Toggle({
  on,
  onChange,
  label,
}: {
  on: boolean;
  onChange: (v: boolean) => void;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-label={label}
      class={`fx-toggle${on ? ' is-on' : ''}`}
      onClick={() => onChange(!on)}
    >
      <i />
    </button>
  );
}

export function Empty({ title, children }: { title: string; children?: ComponentChildren }) {
  return (
    <div class="fx-empty">
      <div class="fx-empty-mark" />
      <div class="fx-empty-t">{title}</div>
      {children && <div class="fx-empty-d">{children}</div>}
    </div>
  );
}

/** Reward line: XP, silver, gold, item cells with counts, and a title badge. */
export function Rewards({
  xp,
  silver,
  gold,
  items = [],
  title,
}: {
  xp?: number;
  silver?: number;
  gold?: number;
  items?: { id: string; count: number }[];
  title?: string;
}) {
  return (
    <span class="fx-chips">
      {xp ? <span class="fx-chip xp">{fmt(xp)} XP</span> : null}
      {silver ? <Amount kind="silver" n={silver} /> : null}
      {gold ? <Amount kind="gold" n={gold} /> : null}
      {items.map((it) => (
        <span key={it.id} class="fx-chip">
          <Cell item={it.id} size={22} />×{compact(it.count)}
        </span>
      ))}
      {title && <span class="fx-title-chip">{title}</span>}
    </span>
  );
}
